import type { Job } from "../types/job";

// Pure extraction of the socket-merge logic from
// aidea.asyncwidget/src/App.tsx:232-313 ("session_job_update" /
// "session_job_completed" handlers). No setState, no socket, no debounce —
// the caller (App.tsx socket handler) reacts to `unknownJob` to trigger the
// debounced history refetch that lived alongside this logic upstream.
//
// ponytail: `trainingId` on the synthesized job used the component's
// sessionId/trainingId closure state upstream, which a pure function can't
// see — falls back to `data.trainingId` (else "").

// Errors come as a plain string (most job types), as { code, message }
// (talking-avatar progress/failure updates), or as a string next to
// metadata.error { code, message } (the gateway's own inspect/failed events).
// The tray renders job.error as a React child — an object would crash it — so
// `error` is always a string and the machine-readable code goes to `errorCode`
// (metadata.error.code first, then error.code).
export function extractJobError(data: any): { error?: string; errorCode?: string } {
  const out: { error?: string; errorCode?: string } = {};
  if (!data || typeof data !== "object") return out;
  const raw = data.error;
  const md = data.metadata && typeof data.metadata.error === "object" ? data.metadata.error : null;
  if (typeof raw === "string") out.error = raw;
  else if (raw && typeof raw === "object") {
    const msg = raw.message || raw.code;
    out.error = typeof msg === "string" ? msg : "Error";
  }
  const code = (md && md.code) || (raw && typeof raw === "object" ? raw.code : undefined);
  if (typeof code === "string" && code) out.errorCode = code;
  // Only a failure turns metadata.error into a message — other job types may
  // carry a stale metadata.error on ordinary progress updates.
  const failed = String(data.status || "").toLowerCase() === "failed";
  if (out.error === undefined && failed && md && (md.message || md.code)) {
    out.error = md.message || md.code;
  }
  return out;
}

const isSegment = (s: any) =>
  !!s &&
  typeof s === "object" &&
  Number.isFinite(Number(s.done)) &&
  Number.isFinite(Number(s.total));

// History rows (REST) go through the same normalization as socket updates,
// and a talking-avatar row carries its stage/segment in metadata.
export function normalizeJob(job: Job): Job {
  if (!job || typeof job !== "object") return job;
  const md: any = job.metadata && typeof job.metadata === "object" ? job.metadata : null;
  const objectError = !!job.error && typeof job.error === "object";
  const mdCode = md && md.error && typeof md.error === "object" ? md.error.code : undefined;
  const liftStage = !job.stage && md && typeof md.stage === "string";
  const liftSegment = !job.segment && md && isSegment(md.segment);
  if (!objectError && !(mdCode && !job.errorCode) && !liftStage && !liftSegment) return job;
  const err = extractJobError(job);
  return {
    ...job,
    ...(objectError || err.error !== undefined ? { error: err.error ?? null } : {}),
    ...(err.errorCode && !job.errorCode ? { errorCode: err.errorCode } : {}),
    ...(liftStage ? { stage: md.stage } : {}),
    ...(liftSegment ? { segment: md.segment } : {}),
  };
}

export function applySessionJobUpdate(
  jobs: Job[],
  data: any
): { jobs: Job[]; unknownJob: boolean } {
  const jobIdToFind = data.jobId || data.id;
  const exists = jobs.some((j) => j.id === jobIdToFind);
  const err = extractJobError(data);

  if (exists) {
    const updatedJobs = jobs.map((j) =>
      j.id === jobIdToFind
        ? {
            ...j,
            status: data.status ?? j.status,
            progress: data.progress ?? j.progress,
            metadata: data.metadata ?? j.metadata,
            updatedAt: new Date().toISOString(),
            error: err.error ?? j.error,
            errorCode: err.errorCode ?? j.errorCode,
            // talking-avatar: which stage, and "Segment x/y" of the video chain.
            stage: data.stage ?? j.stage,
            segment: isSegment(data.segment) ? data.segment : j.segment,
            // approve edildi bilgisi widget dışından (ör. Merlin popup'ının
            // kendi approveJob çağrısından) gelebilir — bu alan eksikse
            // job kuyrukta approved=false ile takılı kalır (bkz. queueOrdering).
            approved: data.approved ?? j.approved,
          }
        : j
    );
    return { jobs: updatedJobs, unknownJob: false };
  }

  // Video Director has its own drafts UI — never a tray card.
  if ((data.jobType || data.type) === "video-director") return { jobs, unknownJob: false };

  const newJob: Job = {
    id: jobIdToFind,
    type: data.jobType || data.type || "text",
    status: data.status,
    progress: data.progress || 0,
    resultUrl: data.result?.url || "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    boxId: "",
    approved: false,
    userId: "",
    trainingId: data.trainingId || "",
    metadata: data.metadata,
    prompt: data.prompt,
    ...(data.navId ? { navId: data.navId } : {}),
    ...(data.stage ? { stage: data.stage } : {}),
    ...(isSegment(data.segment) ? { segment: data.segment } : {}),
    ...(err.error !== undefined ? { error: err.error } : {}),
    ...(err.errorCode ? { errorCode: err.errorCode } : {}),
  };
  return { jobs: [...jobs, newJob], unknownJob: true };
}

export function applySessionJobCompleted(
  jobs: Job[],
  data: any
): { jobs: Job[]; unknownJob: boolean } {
  const jobIdToFind = data.jobId || data.id;
  const exists = jobs.some((j) => j.id === jobIdToFind);

  if (exists) {
    const updatedJobs = jobs.map((j) =>
      j.id === jobIdToFind
        ? {
            ...j,
            status: "inspect",
            progress: 100,
            resultUrl: data.result?.url ?? j.resultUrl,
            result: data.result,
            metadata: data.metadata ?? j.metadata,
            updatedAt: new Date().toISOString(),
            approved: data.approved ?? j.approved,
          }
        : j
    );
    return { jobs: updatedJobs, unknownJob: false };
  }

  return { jobs, unknownJob: true };
}
